import React, { useEffect, useMemo, useState } from 'react';
import {
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  TabPane,
  Button,
  FormFeedback,
} from 'reactstrap';
import * as Yup from 'yup';
import moment from 'moment';
import { useFormik } from 'formik';
import { useMutation } from 'react-query';
import TableContainer from '../../../components/Common/TableContainer';
import { useDispatch } from 'react-redux';
import { getAllRelationshipsApi } from 'api/report';
import { addDependant, uploadDepeMedia } from 'api/member';
import { useAgeLimits } from 'hooks/useAgeLimits';
import notify from 'utils/notify';
import Swal from 'sweetalert2';
import {
  MemberId,
  BillingName,
  Date,
  Total,
  PaymentStatus,
  PaymentMethod,
} from '../MemberCol';
import { DateField, SelectField } from 'components/Common/fields';

const AddDependants = ({ onChange, member, dependents, onNext, mutate, queryClient, onStageDependant }) => {
  const dateFormat = 'DD-MM-YYYY';
  const dispatch = useDispatch();
  const [pages, setPages] = useState(0);
  const [file, setSelectedFile] = useState(null);
  const [pagination, setPagination] = useState({
    pageSize: 20,
    pageNumber: 1,
    sortBy: 'id',
    ascendingSort: false,
    searchColumns: [
      { name: 'memberId', value: member?.memberId },
      { name: 'endDate', value: moment().format(dateFormat) },
      {
        name: 'startDate',
        value: moment().subtract(1, 'year').format(dateFormat),
      },
    ],
  });

  const {
    mutate: mutateRelationships,
    isLoading: isLoadingRelationships,
    data: relationshipsData,
  } = useMutation((payload) => getAllRelationshipsApi(payload), {
    onSettled: () => {
      queryClient.invalidateQueries('/public/um/setup/relationship');
    },
  });

  const { dependantMinDob, dependantMaxAge, maxDob } = useAgeLimits();

  // Define validation schema
  const validationSchema = Yup.object({
    firstName: Yup.string().required('Please Enter First Name'),
    lastName: Yup.string().required('Please Enter Last Name'),
    relationship: Yup.string().required('Please Select Relationship'),
    gender: Yup.string().required('Please Select Gender'),
    dob: Yup.string()
      .required('Please Select DOB')
      .test('dob-min', `Dependant cannot be older than ${dependantMaxAge} years`, (v) =>
        !v || moment(v).isSameOrAfter(dependantMinDob, 'day'),
      )
      .test('dob-future', 'Date of birth cannot be in the future', (v) =>
        !v || moment(v).isSameOrBefore(maxDob, 'day'),
      ),
    // certificateofbirth: Yup.string().required("Please Select Birth Certificate file"),
  });

  // Initialize formik
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      relationship: '',
      mobileNumber: '',
      gender: '',
      dob: '',
      certificateofbirth: '',
    },
    validationSchema,
    onSubmit: async ({ dob, ...values }) => {
      let depId = null;
      const data = {
        ...values,
        memberId: member?.memberId,
        dob: moment(dob).format('DD-MM-YYYY'),
      };
      if (!member?.memberId && typeof onStageDependant === 'function') {
        if (!onStageDependant({ ...data, memberId: undefined, status: 'New' }, file?.name ? file : null)) return;
        Swal.fire('Added', 'Dependant will be saved when the declaration is signed.', 'success');
        validation.resetForm();
        setSelectedFile(null);
        return;
      }
      try {
        const res = await addDependant(data);
        if (res.status === 'Success') {
          depId = res?.data?.id;
          mutate({ memberId: member?.memberId });
          Swal.fire("Success", res?.message, 'success');
          validation.resetForm();
        }
      } catch (err) {
        notify.warning('Dependant not added', err?.message || 'Something went wrong. Please try again.');
        return;
      }
      depId && file && uploadDepeMedia(depId, file);
      // onNext();
    },
  });

  // const addNewDependant = () => {
  //   const newDependant = {
  //     title: `Dependant ${appMember.dependants.length + 1}`,
  //     firstName: '',
  //     lastName: '',
  //     gender: '',
  //     email: '',
  //     dob: new Date().getDate(),
  //     mobileNumber: '',
  //     relationship: '',
  //   };
  //   dispatch(editMember([...appMember.dependants, newDependant], 'dependant'));
  // };

  // const removeDependant = (index) => {
  //   const updatedDependants = [...appMember.dependants];
  //   updatedDependants.splice(index, 1);
  //   dispatch(editMember(updatedDependants, 'dependant'));
  // };

  // const handleDependantChange = (index, key, value) => {
  //   const updatedDependants = [...appMember.dependants];
  //   updatedDependants[index][key] = value;
  //   dispatch(editMember(updatedDependants, 'dependant'));
  // };

  // const handleAddDependant = async () => {
  //   try {
  //     const { dependants } = appMember;
  //     let actions = [];
  //     dependants &&
  //       dependants.map((d) => {
  //         omit(d, ['title']);
  //         d['memberId'] = member.memberId;
  //         d['dob'] = moment(d.dob).format('MM-DD-YYYY');
  //         actions.push(addDependant(d));
  //       });
  //     Promise.all(actions)
  //       .then(() => {
  //         onNext();
  //       })
  //       .catch((err) => {
  //         console.log(err);
  //       });
  //   } catch (error) {
  //     console.log(error);
  //   }
  // };

  useEffect(() => {
    mutateRelationships({
      type: 'Nuclear',
    });
  }, []);

  const handleFileChange = (e) => {
    setSelectedFile(e.currentTarget.files[0]);
    validation.handleChange(e);
  };

  const columns = useMemo(
    () => [
      {
        Header: '#',
        filterable: false,
        disableFilters: true,
        Cell: (cellProps) => {
          return <input type="checkbox" className="form-check-input" />;
        },
      },
      {
        Header: 'FULL NAMES',
        accessor: (row) => `${row.firstName || ''} ${row.lastName || ''}`,
        width: '150px',
        style: { textAlign: 'center', width: '10%', background: '#0000' },
        filterable: true,
        Cell: (cellProps) => <span>{cellProps.value}</span>,
      },
      {
        Header: 'Relationship',
        accessor: 'relationship',
        disableFilters: true,
        filterable: false,
        Cell: (cellProps) => <BillingName {...cellProps} />,
      },
      {
        Header: 'Gender',
        accessor: 'gender',
        disableFilters: true,
        filterable: false,
        Cell: (cellProps) => <Date {...cellProps} />,
      },
      {
        Header: 'Age(Yrs)',
        accessor: 'dob',
        disableFilters: true,
        filterable: false,
        Cell: (cellProps) => {
          const dateValue = moment(cellProps.value, 'DD-MM-YYYY');
          const formattedDate = cellProps.value
            ? moment().diff(dateValue, 'year')
            : 'No DOB Set';
          return <span>{formattedDate}</span>;
        },
      },
      {
        Header: 'Birth Certificate No',
        accessor: 'birthCertificationNumber',
        disableFilters: true,
        filterable: false,
        Cell: (cellProps) => <Total {...cellProps} />,
      },
      {
        Header: 'Status',
        accessor: 'status',
        hidden: false,
        disableFilters: true,
        filterable: false,
        Cell: (cellProps) => {
          const dep = cellProps.row.original;
          const rel = dep?.relationship?.toLowerCase();
          const overEighteen =
            dep?.status !== 'Deceased' &&
            rel !== 'husband' &&
            rel !== 'wife' &&
            dep?.dob &&
            moment().diff(moment(dep.dob, 'DD-MM-YYYY'), 'year') >= 18;
          return (
            <>
              <PaymentStatus {...cellProps} />
              {overEighteen && (
                <span className="badge font-size-12 badge-soft-warning ms-2">Over 18</span>
              )}
            </>
          );
        },
      },
      {
        Header: 'Phone Number',
        accessor: 'mobileNumber',
        disableFilters: true,
        Cell: (cellProps) => <PaymentMethod {...cellProps} />,
      },
    ],
    [],
  );

  return (
    <div>
      <Form
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
      >
        <Row>
          <Col md="6">
            <FormGroup className="mb-3">
              <Label for={`firstName`}>First Name</Label>
              <Input
                type="text"
                name={`firstName`}
                className="form-control"
                id={`firstName`}
                placeholder="Enter First Name"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.firstName || ''}
                invalid={
                  validation.touched.firstName && validation.errors.firstName
                    ? true
                    : false
                }
              />
              {validation.touched.firstName && validation.errors.firstName ? (
                <FormFeedback type="invalid">
                  {validation.errors.firstName}
                </FormFeedback>
              ) : null}
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup className="mb-3">
              <Label for={`lastName`}>Last Name</Label>
              <Input
                type="text"
                className="form-control"
                name={`lastName`}
                id={`lastName`}
                placeholder="Enter Last Name"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.lastName || ''}
                invalid={
                  validation.touched.lastName && validation.errors.lastName
                    ? true
                    : false
                }
              />
              {validation.touched.lastName && validation.errors.lastName ? (
                <FormFeedback type="invalid">
                  {validation.errors.lastName}
                </FormFeedback>
              ) : null}
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md="6">
            <FormGroup className="mb-3">
              <Label for={`gender`}>Sex</Label>
              <SelectField
                id={`gender`}
                name="gender"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.gender || ''}
                invalid={
                  validation.touched.gender && validation.errors.gender
                    ? true
                    : false
                }
              >
                <option defaultValue>Select Gender...</option>
                <option value="Female">Female</option>
                <option value="Male">Male</option>
                <option value="Intersex">Intersex</option>
                <option value="Other">Other</option>
              </SelectField>
              {validation.touched.gender && validation.errors.gender ? (
                <FormFeedback type="invalid">
                  {validation.errors.gender}
                </FormFeedback>
              ) : null}
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup className="mb-3">
              <Label for={`dob`}>Date Of birth</Label>
              <DateField
                name={`dob`}
                id={`dob`}
                placeholder="Select Date of Birth"
                min={dependantMinDob}
                max={maxDob}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.dob || ''}
                invalid={
                  validation.touched.dob && validation.errors.dob ? true : false
                }
              />
              {validation.touched.dob && validation.errors.dob ? (
                <FormFeedback type="invalid">
                  {validation.errors.dob}
                </FormFeedback>
              ) : null}
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md="6">
            <FormGroup className="mb-3">
              <Label for={`mobileNumber`}>Phone Number</Label>
              <Input
                type="text"
                className="form-control"
                id={`mobileNumber`}
                placeholder="Enter Phone Number"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.mobileNumber || ''}
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup className="mb-3">
              <Label>Relationship</Label>
              <SelectField
                name="relationship"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.relationship || ''}
                invalid={
                  validation.touched.relationship &&
                    validation.errors.relationship
                    ? true
                    : false
                }
              >
                <option defaultValue value={''}>
                  Select Relationship...
                </option>
                {relationshipsData &&
                  relationshipsData.map((item) => (
                    <option key={item.id} value={item.name}>
                      {item.name}
                    </option>
                  ))}
              </SelectField>
              {validation.touched.relationship &&
                validation.errors.relationship ? (
                <FormFeedback type="invalid">
                  {validation.errors.relationship}
                </FormFeedback>
              ) : null}
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup className="mb-3">
              <Label for={`email`}>Enter Email Address</Label>
              <Input
                name={`email`}
                type="text"
                className="form-control"
                id={`email`}
                placeholder="Enter Email Address"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.email || ''}
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <div className="mb-3">
              {/* Conditionally render label based on the selected relationship */}
              <Label for="certificateofbirth">
                {validation.values.relationship === 'Husband' ||
                  validation.values.relationship === 'Wife'
                  ? 'Upload Dependent Identification Card'
                  : 'Upload Dependent Birth Certificate'}
              </Label>
              <Input
                type="file"
                name="certificateofbirth"
                className="form-control"
                id="certificateofbirth"
                value={validation.values.certificateofbirth || ''}
                placeholder={
                  validation.values.relationship === 'Husband' ||
                    validation.values.relationship === 'Wife'
                    ? 'Upload Dependent Identification Card'
                    : 'Upload Dependent Birth Certificate'
                }
                onChange={handleFileChange}
              // invalid={validation.touched.certificateofbirth && validation.errors.certificateofbirth ? true : false}
              />
              {/* {validation.touched.certificateofbirth && validation.errors.certificateofbirth ? (
                                <FormFeedback type="invalid">{validation.errors.certificateofbirth}</FormFeedback>
                            ) : null} */}
            </div>
          </Col>
        </Row>
        {/* <p
                        style={{
                            color: "red",
                            textDecoration: "underline",
                            cursor: "pointer",
                            marginTop: "5px",
                        }}
                        onClick={() => removeDependant(index)}
                    >
                        Remove Dependant
                    </p> */}
        {/* </div>
                    ))} */}
        <div className="wizard-actions">
          <Button type="submit" color="primary">
            <i className="mdi mdi-plus me-1" />
            Save and add another
          </Button>
        </div>
        <div className="d-flex align-items-center justify-content-between mt-4 mb-3">
          <h6 className="mb-0 fw-semibold">
            Dependants added
            <span className="badge bg-light text-body ms-2">{dependents?.length || 0}</span>
          </h6>
        </div>
        <Row>
          <Col xs="12">
          <TableContainer
            columns={columns}
            pages={1}
            data={dependents || []}
            isGlobalFilter={false}
            isAddOptions={false}
            isPagination={true}
            iscustomPageSizeOptions={false}
            customPageSize={4}
            // showEdit
            // showDelete
            // handleEdit={editDependent}
            // showView
            paginationData={pagination}
            onChangePagination={() => { }}
            tableClass="align-middle table-nowrap"
            theadClass="table-light"
          />
          </Col>
        </Row>
      </Form>
    </div >
  );
};

export default AddDependants;
